/* global ACPRS_BASE_URL */

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import data from '../../../../assets/data.json';
import UnderlineButton from '../../components/underlineButton/underlineButton';
import styles from './resultsStep.module.css';
import ExportButton from '../../components/exportButton/exportButton';
import IconLink from '../../../img/icons/link.svg';
import SliderIcon from '../../../img/icons/sliders.svg';
import CloseIcon from '../../../img/icons/close.svg';
import PlusIcon from '../../../img/icons/plus.svg';
import MinusIcon from '../../../img/icons/minus.svg';
import GreenArrowDown from '../../../img/icons/green-arrow-down.svg';
import CloseModalIcon from '../../../img/icons/close-cross.svg';
import CopyLink from '../../components/copyLink/copyLink';
import ColorButton from '../../components/colorButton/colorButton';
import ArrowLight from '../../../img/icons/arrow-light.svg';
import ChevronDown from '../../../img/icons/chevron-down.svg';
import { ResultsSummary } from '../../components/ResultsSummary/ResultsSummary';
import { strExtractLast } from '../../utils/string.mjs';

/**
 * Turn a given string into something compatible for an id.
 * @param {string} string
 * @param {prefix} string
 * @return {string}
 **/
const strToId = (string, prefix = '') => {
  return (
    prefix +
    string
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
  );
};

const ResultsCardHook = ({
  subdivision,
  section,
  citation,
  citationHref,
  summary,
  extract,
  type,
  requirement,
  rules = [],
  item,
  userIsProvider = false,
  pageNumber,
  topic,
  ...props
}) => {
  const hasRules = rules.length > 0;
  return (
    <article className={styles.cardContainer} {...props}>
      <div className={styles.primaryContentWrapper}>
        <div className={styles.paddingWrapper}>
          <div className={styles.subDivisionAndSectionWrapper}>
            <div className={styles.subdivisionContainer}>
              <p className={styles.subdivisionLabel}>Act Subdivision</p>
              <p className={styles.subdivisionName}>{subdivision}</p>
            </div>
            <span className={styles.divider} />

            <div className={styles.sectionContainer}>
              <p className={styles.sectionLabel}>Act Section</p>
              <p className={styles.sectionName}>{section}</p>
            </div>
          </div>
          <div>
            <a
              className={styles.citationLink}
              href={citationHref}
              target='_blank'
              rel='noopener noreferrer'
            >
              <span className={styles.citationAndLinkContainer}>
                {citation}
                <IconLink aria-hidden />
              </span>
            </a>
          </div>
          <div className={styles.summary}>{extract}</div>
        </div>
        {hasRules && <ResultsCardAccordion citation={citation} items={rules} userIsProvider={userIsProvider} pageNumber={pageNumber} topic={ topic } />}
      </div>
    </article>
  );
};

const ResultsCardAccordion = ({ citation, items, userIsProvider = false, pageNumber, topic, ...props }) => {
  const [expanded, setExpanded] = useState(false);
  const handleClick = () => {
    setExpanded(!expanded);
  };

  let classNameBtn = `au-accordion__title js-au-accordion ${styles.accordionButton}`;
  let classNameBody = 'au-accordion__body ';
  if (expanded) {
    classNameBtn += ` au-accordion--open ${styles.accordionOpenOverride}`;
    classNameBody += ` au-accordion--open ${styles.accordionOpenOverride}`;
  } else {
    classNameBtn += ' au-accordion--closed';
    classNameBody += ' au-accordion--closed';
  }
  /**
   * Maps from the category data and returns an array of registered categories
   * that the rule applies to.
   * @param {registeredCategories}
   * @return {array}
   **/
  const mapCategory = (registeredCategories) => {
    const categories = [
      {
        category: 'cat1',
        label: 'Category 1 - Home and Community Services',
        isApplicable: registeredCategories.cat1.category
      },
      {
        category: 'cat2',
        label: 'Category 2 - Assistive technology and home modifications',
        isApplicable: registeredCategories.cat2.category
      },
      {
        category: 'cat3',
        label: 'Category 3 - Advisory and support services',
        isApplicable: registeredCategories.cat3.category
      },
      {
        category: 'cat4',
        label:
          'Category 4 - Personal care and care support in the home or community',
        isApplicable: registeredCategories.cat4.category
      },
      {
        category: 'cat5',
        label: 'Category 5 - Nursing and transition care',
        isApplicable: registeredCategories.cat5.category
      },
      {
        category: 'cat6',
        label: 'Category 6 - Residential care',
        isApplicable: registeredCategories.cat6.category
      }
    ];
    return categories;
  };
  useEffect(() => { 
    setExpanded(false)
  }, [pageNumber,topic])
  return (
    <section
      className={`au-accordion au-accordion--block ${styles.auAccordionOverride}`}
      {...props}
      style={{ marginBottom: '0px', marginTop: '0px' }}
    >
      <button
        className={classNameBtn}
        aria-controls={strToId(citation, 'accordion-body-citation--')}
        aria-expanded={expanded}
        id={strToId(citation, 'accordion-toggle-citation--')}
        onClick={handleClick}
        type='button'
      >
        {!expanded ? 'Expand to view' : 'Collapse'} associated rules
        <span className='sr-only'> for {citation}</span>
      </button>

      <div
        className={classNameBody}
        id={strToId(citation, 'accordion-body-citation--')}
        aria-labelledby={strToId(citation, 'accordion-toggle-citation--')}
      >
        <div
          className={`au-accordion__body-wrapper ${styles.accordionBodyWrapperOverride}`}
        >
          <ul className={styles.accordionBodyWrapper}>
            {items.map((rule) => {
              const mappedRuleCategories = mapCategory(
                rule.registeredCategories
              );

              const ruleHasCategoriesOrAllProviders =
                mappedRuleCategories.filter(
                  (category) => category?.isApplicable
                ).length > 0 || (userIsProvider && rule.allProviders);
              return (
                <li key={rule.id} className={styles.ruleContainer}>
                  <div className={styles.ruleMeta}>
                    <h4 className={styles.sourceSubHeader}>Source</h4>
                    <span className={styles.source}>{rule.source}</span>
                    <h4 className='sr-only'>Citation</h4>
                    <a
                      className={styles.sourceCitationLink}
                      href={rule.link}
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      {rule.citation}
                    </a>
                  </div>
                  <div className={styles.ruleContent}>
                    <div dangerouslySetInnerHTML={{ __html: rule.summaryHTML }} />
                    {ruleHasCategoriesOrAllProviders && (
                      <div>
                        <h4 className='sr-only'>Applicable categories</h4>
                        <ul className={styles.categoryList} role='list'>
                          {rule.allProviders && (
                            <li className={styles.categoryBox}>
                              All registered providers
                            </li>
                          )}
                          {mappedRuleCategories.map(
                            (category) =>
                              category.isApplicable && (
                                <li
                                  key={category.category}
                                  className={styles.categoryBox}
                                >
                                  {category.label}
                                </li>
                              )
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
};

const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  const getVisiblePages = () => {
    const delta = 2; // how many pages to show on each side of current page
    const range = [];
    const rangeWithDots = [];

    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  const getMobileVisiblePages = () => {
    // For mobile, show max 2 pages around current page
    const pages = [];
    const startPage = Math.max(1, currentPage - 1);
    const endPage = Math.min(totalPages, currentPage + 1);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  if (totalPages <= 1) return null;

  return (
    <nav
      id='pagination-nav'
      className={styles.paginationContainer}
      aria-label='Pagination'
    >
      {/* Desktop pagination */}
      <div className={styles.paginationDesktop}>
        <button
          type='button'
          className={styles.paginationButton}
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          aria-label='Go to first page'
        >
          « first
        </button>

        <button
          type='button'
          className={styles.paginationButton}
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          aria-label='Go to previous page'
        >
          « previous
        </button>

        {getVisiblePages().map((page, index) =>
          page === '...'
            ? (
              <span
                key={`ellipsis-${index}`}
                className={styles.paginationEllipsis}
              >
                ...
              </span>
              )
            : (
              <button
                type='button'
                key={page}
                className={`${styles.paginationButton} ${
                currentPage === page ? styles.paginationButtonActive : ''
              }`}
                onClick={() => onPageChange(page)}
                aria-label={`Go to page ${page}`}
                aria-current={currentPage === page ? 'page' : undefined}
              >
                {page}
              </button>
              )
        )}

        <button
          type='button'
          className={styles.paginationButton}
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          aria-label='Go to next page'
        >
          next »
        </button>

        <button
          type='button'
          className={styles.paginationButton}
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
          aria-label='Go to last page'
        >
          last »
        </button>
      </div>

      {/* Mobile pagination */}
      <div className={styles.paginationMobile}>
        <button
          type='button'
          className={styles.paginationButtonMobile}
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          aria-label='Go to previous page'
        >
          « previous
        </button>

        <div className={styles.paginationPagesMobile}>
          {getMobileVisiblePages().map((page) => (
            <button
              type='button'
              key={page}
              className={`${styles.paginationButtonMobile} ${
                currentPage === page ? styles.paginationButtonMobileActive : ''
              }`}
              onClick={() => onPageChange(page)}
              aria-label={`Go to page ${page}`}
              aria-current={currentPage === page ? 'page' : undefined}
            >
              {page}
            </button>
          ))}
        </div>

        <button
          type='button'
          className={styles.paginationButtonMobile}
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          aria-label='Go to next page'
        >
          next »
        </button>
      </div>
    </nav>
  );
};

const DisclaimerModal = ({ setShowDisclaimerModal, handleGoBack }) => {
  return (
    <div className={styles.disclaimerModalBackground}>
      <div className={styles.disclaimerModalContainer}>
        <p className={styles.disclaimerModalTitle}>Disclaimer</p>
        <p className={styles.disclaimerModalText}>
          Please be aware that if you do not answer all the questions provided,
          you may not receive results that are relevant to your situation. For
          the most accurate results and comprehensive information, we encourage
          you to answer all questions. The requirements based on your current
          selections will be displayed on the following results page.
        </p>
        <div className={styles.disclaimerModalButtonContainer}>
          <UnderlineButton label='Go back' onClick={handleGoBack} />
          <ColorButton
            onClick={(e) => {
              e.preventDefault();
              setShowDisclaimerModal(false);
            }}
            label='Continue to results page'
            iconRight={<ArrowLight className={styles.arrowIcon} />}
          />
        </div>
      </div>
    </div>
  );
};

export default function ResultsStep ({
  formData,
  setFormData,
  handleEditDetails
}) {
  const dataReq = data.data;
  const navigate = useNavigate();
  const initialFormState = {
    conditions: false,
    obligations: false,
    statutory: false
  };
  const [formState, setFormState] = useState(initialFormState);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isPrinting, setIsPrinting] = useState(false);
  const resultsPerPage = 10;

  const [exportURL, setExportURL] = useState(
    ACPRS_BASE_URL + '/acprr-tool/?'
  );
  const [isOpen, setIsOpen] = useState([false, false, false, false]);
  const setInitialRequirements = () => {
    setFormState(initialFormState);
    setSelectedSections(initialSections);
    setSectionSearchValue('');
    setExpandedSubdivisions(initialExpandedSubdivisions);
    setShowAllSubdivisions(false);
    handleTopicChange(topicOptions[0]);
    setCurrentPage(1); // Reset pagination when filters change
  };
  const [allReqs, setAllReqs] = useState(true);
  const handleRequirementsChange = (event) => {
    setFormState({
      ...formState,
      [event.target.value]: event.target.checked
    });
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleGoBack = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const currentStep = parseInt(searchParams.get('step'), 10);

    if (!isNaN(currentStep) && currentStep > 1) {
      searchParams.set('step', 3);
      window.location.href = `${
        window.location.pathname
      }?${searchParams.toString()}`;
    }
  };

  // Filter formData to show only checked registered categories
  const userTopicOnly = !(
    formData.descriptionStep.isValid ||
    formData.fundingStep.isValid ||
    formData.govEntityStep.isValid ||
    formData.providerStep.isValid ||
    formData.locationStep.isValid ||
    formData.servicesStep.isValid
  );

  // For other users that are interested in results:
  // - descriptionStep: other
  // - servicesStep
  // - optional:
  //   - fundingStep
  //   - govEntityStep

  const noResponseGovt = !formData.govEntityStep.isValid;
  const noResponseFunding = !formData.fundingStep.isValid;

  const selectedCategories = formData.servicesStep.serviceCategories.filter(
    (item) => item.isSelected
  );
  const selectedFunding = formData.fundingStep.fundingSource.filter(
    (item) => item.isChecked
  );

  const userIsProvider =
    formData.providerStep.provider === 'prospective' ||
    formData.providerStep.provider === 'registered';

  const userIsUnspecified = !formData.descriptionStep.isValid;

  const dataFilteredByCategory = dataReq
    .filter((item) => {
      if (userTopicOnly || userIsUnspecified) return true;
      if (userIsProvider) return item.individualProvider === 'Provider';
      else return item.individualProvider === 'Individual';
    })
    .filter((item) => {
      let result = false;
      if ((userIsProvider && item.allProviders) || userTopicOnly) {
        return true;
      }
      selectedCategories.forEach((category) => {
        if (result) return;
        switch (category.id) {
          case 'CAT1':
            result = item.registeredCategories.cat1.category;
            break;
          case 'CAT2':
            result = item.registeredCategories.cat2.category;
            break;
          case 'CAT3':
            result = item.registeredCategories.cat3.category;
            break;
          case 'CAT4':
            result = item.registeredCategories.cat4.category;
            break;
          case 'CAT5':
            result = item.registeredCategories.cat5.category;
            break;
          case 'CAT6':
            result = item.registeredCategories.cat6.category;
            break;
          default:
            result = false;
            break;
        }
      });
      if (!result) {
        formData.servicesStep.serviceCategories.forEach((category) => {
          if (result) return;
          const selectedSubcategories = category.subCategories.filter(
            (subcategory) => subcategory.isSelected
          );
          selectedSubcategories.forEach((selectedSubcategory) => {
            // eslint-disable-next-line no-unused-vars
            for (const [_key, value] of Object.entries(
              item.registeredCategories
            )) {
              if (result) return;
              const hasSubcategory =
                selectedSubcategory.id.toLowerCase() in value.subcategories &&
                value.subcategories[selectedSubcategory.id.toLowerCase()];
              result = hasSubcategory;
            }
          });
        });
      }
      return result;
    })
    .filter((item) => {
      // Filter by funding
      let result = false;
      // items with allProvider don't have a source, so return as true by default
      if (userTopicOnly || noResponseFunding || (userIsProvider && item.allProviders)) return true;
      selectedFunding.forEach((source) => {
        if (result) return;
        if (source.value === 'residential' && item.cohort.resi) result = true;
        else if (source.value === 'support' && item.cohort.sah) result = true;
        else if (source.value === 'CHSP' && item.cohort.chsp) result = true;
        else if (source.value === 'NATSIFACP' && item.cohort.natsifacp) {
          result = true;
        } else if (source.value === 'MPSP' && item.cohort.mps) result = true;
        else if (source.value === 'TCP' && item.cohort.tcp) result = true;
        else result = false;
      });
      return result;
    })
    .filter((item) => {
      if (userTopicOnly || noResponseGovt || (userIsProvider && item.allProviders)) return true;
      let result = false;
      const applicableGovt = (item.providerType.bodyPolitic || item.providerType.govt);
      const applicableNonGovt = (
        item.providerType.individual ||
          item.providerType.bodyCorp ||
          item.providerType.partnerships ||
          item.providerType.otherUnico
      );
      switch (formData.govEntityStep.isGovEntity[0].isChecked) {
        case true:
          result = applicableGovt;
          break;
        case false:
        default:
          result = applicableNonGovt;
          break;
      }
      return result;
    });

  // Requirements: All, Conditions, Obligations, Statutory duties
  const checkboxRequirements = [
    {
      id: 'requirementType--conditions',
      value: 'conditions',
      label: 'Conditions of registration'
    },
    {
      id: 'requirementType--obligations',
      value: 'obligations',
      label: 'Obligations'
    },
    {
      id: 'requirementType--statutory',
      value: 'statutory',
      label: 'Statutory duties'
    }
  ];

  useEffect(() => {
    // if none or all are selected, that means no filtering on requirements is needed
    const requirementsFilterCount = Object.values(formState).reduce(
      (acc, item) => acc + (item === true ? 1 : 0),
      0
    );
    setAllReqs(requirementsFilterCount >= 3 || requirementsFilterCount === 0);
  }, [formState]);

  // These are checked against the actual params to see if these steps were skipped
  const disclaimerQualifyingParams = ['g', 'f'];

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const isOtherProvider = searchParams.get('p') === 'other';
    const descriptionParam = searchParams.get('d');
    const missingParams = disclaimerQualifyingParams.filter(
      (param) => !searchParams.has(param)
    );

    if (isOtherProvider && missingParams.length > 0 && !descriptionParam) {
      setShowDisclaimerModal(true);
    }
  }, []);

  /**
   * For a given hook, return the same hook but only filtered on categories.
   *
   * @param {object} hook
   * @param {boolean} userTopicOnly
   * @param {boolean} userIsProvider
   * @return {object} hook, with updated rules
   **/
  const mapHookRulesByCategory = (hook = {}, userTopicOnly = false, userIsProvider = false) => {
    // selectedSubcategories = category.subCategories.filter
    const rulesFiltered = hook?.rules
      ?.filter((rule) => {
        let result = false;
        if ((userIsProvider && rule.allProviders) || userTopicOnly || !userIsProvider) return true;
        selectedCategories.forEach((category) => {
          if (result) return;
          switch (category.id) {
            case 'CAT1':
              result = rule.registeredCategories.cat1.category;
              break;
            case 'CAT2':
              result = rule.registeredCategories.cat2.category;
              break;
            case 'CAT3':
              result = rule.registeredCategories.cat3.category;
              break;
            case 'CAT4':
              result = rule.registeredCategories.cat4.category;
              break;
            case 'CAT5':
              result = rule.registeredCategories.cat5.category;
              break;
            case 'CAT6':
              result = rule.registeredCategories.cat6.category;
              break;
            default:
              result = false;
              break;
          }
        });
        if (!result) {
          formData.servicesStep.serviceCategories.forEach((category) => {
            if (result) return;
            const selectedSubcategories = category.subCategories.filter(
              (subcategory) => subcategory.isSelected
            );
            // For each user selected subcategory
            selectedSubcategories.forEach((selectedSubcategory) => {
              // eslint-disable-next-line no-unused-vars
              for (const [_key, value] of Object.entries(
                rule.registeredCategories
              )) {
                if (result) return;
                // match subcategory against selectedSubcategory.id
                const hasSubcategory =
                  selectedSubcategory.id.toLowerCase() in value.subcategories &&
                  value.subcategories[selectedSubcategory.id.toLowerCase()];
                result = hasSubcategory;
              }
            });
          });
        }
        return result;
      })
      .filter((item) => {
        // Filter by funding
        let result = false;
        // items with allProvider don't have a source, so return as true by default
        if (userTopicOnly || noResponseFunding || (userIsProvider && item.allProviders) || !userIsProvider) {
          return true;
        }
        selectedFunding.forEach((source) => {
          if (result) return;
          if (source.value === 'residential' && item.cohort.resi) result = true;
          else if (source.value === 'support' && item.cohort.sah) result = true;
          else if (source.value === 'CHSP' && item.cohort.chsp) result = true;
          else if (source.value === 'NATSIFACP' && item.cohort.natsifacp) {
            result = true;
          } else if (source.value === 'MPSP' && item.cohort.mps) result = true;
          else if (source.value === 'TCP' && item.cohort.tcp) result = true;
          else result = false;
        });
        return result;
      })
      .filter((item) => {
        if (userTopicOnly || noResponseGovt || (userIsProvider && item.allProviders) || !userIsProvider) return true;
        let result = false;
        const applicableGovt = (item.providerType.bodyPolitic || item.providerType.govt);
        const applicableNonGovt = (
          item.providerType.individual ||
            item.providerType.bodyCorp ||
            item.providerType.partnerships ||
            item.providerType.otherUnico
        );
        switch (formData.govEntityStep.isGovEntity[0].isChecked) {
          case true:
            result = applicableGovt;
            break;
          case false:
          default:
            result = applicableNonGovt;
            break;
        }
        return result;
      })
      .filter((rule) => {
        if (userIsProvider || userTopicOnly) return true;
        // else get what the user selected
        const otherUserType = formData.descriptionStep?.value;
        switch (otherUserType) {
          case 'acw':
            return rule.other?.acw;
          case 'dpo':
            return rule.other?.dpo;
          case 'rp':
            return rule.other?.rp;
          default:
            return false;
        }
      })
      .filter(
        (rule, index, array) =>
          index === array.findIndex((o) => o.citation === rule.citation)
      ).filter(
        (rule) => {
          const selectedTopic = formData.resultsStep.topic;
          return selectedTopic === 'all' || rule?.topic?.includes(selectedTopic);
        }
      );

    const result = { ...hook, rules: rulesFiltered };
    return result;
  };

  const filteredData = useMemo(() => {
    // split by topics
    const dataConditions = dataFilteredByCategory.filter(
      (item) => item.requirement === 'Conditions of Registration'
    );
    const dataObligations = dataFilteredByCategory.filter(
      (item) => item.requirement === 'Obligations'
    );
    const dataStatutory = dataFilteredByCategory.filter(
      (item) => item.requirement === 'Statutory Duties'
    );

    const conditionalData = [
      allReqs || formState.conditions ? dataConditions : [],
      allReqs || formState.obligations ? dataObligations : [],
      allReqs || formState.statutory ? dataStatutory : []
    ];

    const filteredData = new Set(
      conditionalData.flat().toSorted((a, b) => {
        const firstExtract = strExtractLast(a.citation);
        const secondExtract = strExtractLast(b.citation);
        return firstExtract.localeCompare(secondExtract, 'en', { numeric: true });
      })
    );

    let result = [];

    const otherUser = formData.descriptionStep?.value;

    const isOtherUser = ['acw', 'dpo', 'rp'].includes(otherUser);
    if (isOtherUser) {
      result = dataReq.filter((hook) => {
        switch (otherUser) {
          case 'acw':
            return hook.other?.acw;
          case 'dpo':
            return hook.other?.dpo;
          case 'rp':
            return hook.other?.rp;
          default:
            return false;
        }
      });
    } else {
      result = [...filteredData];
    }
    return result.map((hook) => mapHookRulesByCategory(hook, userTopicOnly, userIsProvider));
  }, [
    allReqs,
    formState.conditions,
    formState.obligations,
    formState.statutory,
    formData.descriptionStep?.value,
    formData.resultsStep?.topic
  ]);

  /**
   * Check if the hook has a matching section.
   *
   * @param {hook} element
   * @return {boolean}
   */
  const filterSections = (hook) => {
    return selectedSections.get(strToId(hook.section, 'sec--')) === true;
  };

  /**
   * @param {hook} element
   * @return {boolean}
   */
  const filterSubdivisions = (hook) => {
    return selectedSections.get(strToId(hook.subdivision, 'sub--')) === true;
  };

  /**
   * Don't need to filter if all items are selected or none of them.
   * @return {boolean}
   **/
  const filterSectionsAllOrNone = () => {
    const selectedSize = [...selectedSections.values()].filter(
      (item) => item === false
    ).length;
    if (selectedSize === selectedSections.size || selectedSize === 0) {
      return true;
    }
    return false;
  };

  /**
   * Check if the hook matches the selected topic
   *
   * @param {hook} element
   * @return {boolean}
   **/
  const filterTopic = (hook) => {
    const selectedTopic = formData.resultsStep.topic;
    if (selectedTopic === 'all') return true;
    return hook.topic?.includes(selectedTopic);
  };

  const dataSubdivisionPairs = filteredData.map((hook) => {
    const { subdivision, section } = hook;
    return { subdivision, section };
  });

  const onlySubdivisions = new Set(
    dataSubdivisionPairs.map((item) => item.subdivision)
  );

  // The reason this is up here is because the options themselves shouldn't be
  // filtered when we run the filter. Circular, I know.
  const dataSubdivisions = [...onlySubdivisions].map((subdivision) => {
    const results = Map.groupBy(
      dataSubdivisionPairs,
      (item) => item.subdivision === subdivision
    );
    return {
      id: strToId(subdivision, 'sub--'),
      label: subdivision,
      sections: new Set(
        results
          .get(true)
          .map((item) => {
            return { id: strToId(item.section, 'sec--'), label: item.section };
          })
          .filter(
            (elem, index, array) =>
              index === array.findIndex((o) => o.id === elem.id)
          )
      )
    };
  });

  const initialSections = new Map(
    dataSubdivisions
      .flatMap((item) => [
        { id: item.id, label: item.label },
        ...[...item.sections]
      ])
      .map((item) => [item.id, false])
  );

  const [selectedSections, setSelectedSections] = useState(initialSections);

  const handleSubdivisionChange = (event, numberOfSubsecs) => {
    console.log('number of subsecs: ', numberOfSubsecs);
    const section = selectedSections.get(event.target.value);
    const updatedState = new Map(selectedSections);

    // Convert Map keys to an array to find index
    const keys = Array.from(selectedSections.keys());
    const changedIndex = keys.indexOf(event.target.value);

    // Flip the value of the selected section
    updatedState.set(event.target.value, !section);

    // Flip the next `numberOfSubsecs` values (ensure they exist)
    for (let i = 1; i <= numberOfSubsecs; i++) {
      const nextKey = keys[changedIndex + i];
      console.log('nextKey: ', nextKey);
      if (nextKey !== undefined) {
        updatedState.set(nextKey, !section);
      }
    }

    setSelectedSections(updatedState);
    setCurrentPage(1);
  };

  // helper method to check whether all child subsecs of a subdiv are selected
  const allOtherSubsectionsSelected = (
    subsection,
    subdivisionId,
    numberOfSubsecs
  ) => {
    const keys = Array.from(selectedSections.keys());
    const startIndex = keys.indexOf(subdivisionId);

    for (let i = 1; i <= numberOfSubsecs; i++) {
      const siblingKey = keys[startIndex + i];
      // don't check for the subsection triggering the event
      if (siblingKey === subsection) continue;
      if (!selectedSections.get(siblingKey)) {
        return false;
      }
    }
    return true;
  };

  const handleSectionChange = (
    event,
    parentSubdivisionId,
    numberOfSiblingSubsecs
  ) => {
    const section = selectedSections.get(event.target.value);
    const updatedState = new Map(selectedSections);
    updatedState.set(event.target.value, !section);
    if (!section === false) {
      updatedState.set(parentSubdivisionId, false);
    } else {
      if (
        allOtherSubsectionsSelected(
          event.target.value,
          parentSubdivisionId,
          numberOfSiblingSubsecs
        )
      ) {
        updatedState.set(parentSubdivisionId, true);
      }
    }

    setSelectedSections(updatedState);
    setCurrentPage(1);
  };

  const topicOptions = [
    {
      id: 'all',
      label: 'All topics',
      value: 'all'
    },
    {
      id: 'agreements',
      label: 'Agreements, plans and information',
      value: 'agreements'
    },
    {
      id: 'governance',
      label: 'Governance',
      value: 'governance'
    },
    {
      id: 'reporting',
      label: 'Reporting and notifications',
      value: 'reporting'
    },
    {
      id: 'workforce',
      label: 'Workforce',
      value: 'workforce'
    },
    {
      id: 'complaints',
      label: 'Complaints and feedback',
      value: 'complaints'
    }
  ];

  const dataPreTopic = filteredData.filter((hook) => {
    return (
      filterSectionsAllOrNone() ||
      filterSections(hook) ||
      filterSubdivisions(hook)
    );
  });

  /**
   * Returns true if result returns acw|dpo|rp.
   *
   * Needs to also return true if descriptionStep is not filled.
   * OR if we select a topic directly from home.
   *
   * @return {boolean}
   **/
  const filterOther = (hook) => {
    // because it's a object: hook.other.[acw]
    let result = false;
    // pull formData state in.
    const providerType = formData.providerStep?.provider;
    const userIsUnspecified = !formData.descriptionStep.isValid;
    const selectedType = formData.descriptionStep?.value;

    // Exit filter early:
    if (providerType !== 'other' || userIsUnspecified) return true;

    // compare user's selection against the hook
    switch (selectedType) {
      case 'acw':
        if (hook.other?.acw) result = true;
        break;
      case 'dpo':
        if (hook.other?.dpo) result = true;
        break;
      case 'rp':
        if (hook.other?.rp) result = true;
        break;
      default:
        break;
    }

    return result;
  };

  const dataPostFilters = dataPreTopic.filter((hook) => {
    return filterOther(hook) && filterTopic(hook);
  });

  const totalPages = Math.ceil(dataPostFilters.length / resultsPerPage);
  const startIndex = (currentPage - 1) * resultsPerPage;
  const endIndex = startIndex + resultsPerPage;
  const currentResults = dataPostFilters.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);

    setTimeout(() => {
      document
        .getElementById('results-page-topic-menu')
        ?.scrollIntoView({ behavior: 'smooth' });
    }, 10); // adding a tiny delay fixes the scrolling behaviour for 'first' and 'last' pagination buttons
  };

  const initialExpandedSubdivisions = Object.fromEntries(
    dataSubdivisions.map((item) => [item.id, false])
  );

  const [sectionSearchValue, setSectionSearchValue] = useState('');

  const handleSearchChange = (e) => {
    setSectionSearchValue(e.target.value);
  };

  const filteredDataSubdivisions = dataSubdivisions.filter(
    (subdivision) =>
      subdivision.label
        .toLowerCase()
        .includes(sectionSearchValue.toLowerCase()) ||
      [...subdivision.sections].some((section) =>
        section.label.toLowerCase().includes(sectionSearchValue.toLowerCase())
      )
  );

  const [showAllSubdivisions, setShowAllSubdivisions] = useState(false);
  const VISIBLE_LIMIT = 7;

  const [expandedSubdivisions, setExpandedSubdivisions] = useState(
    initialExpandedSubdivisions
  );
  const allExpanded = Object.values(expandedSubdivisions).every(
    (v) => v === true
  );
  const toggleSubdivisions = (id) => {
    setExpandedSubdivisions((prev) => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  const toggleExpandAll = (e) => {
    e.preventDefault();
    const next = Object.fromEntries(
      Object.keys(expandedSubdivisions).map((key) => [key, !allExpanded])
    );
    setExpandedSubdivisions(next);
  };

  const handleTopicChange = (topic) => {
    setFormData((prevData) => ({
      ...prevData,
      resultsStep: {
        topic: topic.value
      }
    }));
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('r', topic.value);
    navigate(`?${searchParams.toString()}`, { replace: true });
    setCurrentPage(1);
  };

  const filterMobileAccordion = (title, content, index) => {
    return (
      <div
        id={`filter-accordion-${index}`}
        className='accordionContainer'
        style={{ width: '100%' }}
      >
        <button
          className={styles.accordionController}
          onClick={() =>
            setIsOpen((prevList) =>
              prevList.map((value, i) => (i === index ? !value : value))
            )}
          type='button'
        >
          <p>{title}</p>
          {!isOpen[index]
            ? (
              <PlusIcon className={styles.accordionIcon} />
              )
            : (
              <MinusIcon className={styles.accordionIcon} />
              )}
        </button>
        {isOpen[index] && (
          <div className={styles.accordionExpandableContainer}>{content}</div>
        )}
      </div>
    );
  };

  const searchParams = new URLSearchParams(window.location.search);

  const shouldDisplayRequirementTypes = searchParams.get('d') === null;

  const filterScopeOfReqs = [
    <fieldset
      id='type-of-requirements'
      className={styles.fieldset}
      key='type-of-reqs'
    >
      <legend className={styles.filterSubheader}>Type of requirements</legend>
      <div className={styles.collapsibleSectionsListContainer}>
        {checkboxRequirements.map((item) => (
          <div key={item.id} style={{ paddingLeft: '4px' }}>
            <label className={styles.expandableLabel}>
              <input
                type='checkbox'
                name='requirementType'
                id={item.id}
                value={item.value}
                checked={formState[item.value]}
                onChange={handleRequirementsChange}
              />
              {item.label}
            </label>
          </div>
        ))}
      </div>
    </fieldset>,
    <fieldset
      id='subdivisions-and-sections'
      className={styles.fieldset}
      key='subdivisions-sections'
    >
      <legend
        className={styles.filterSubheader}
        id='subdivisions-and-sections--legend'
      >
        Subdivisions and sections
      </legend>
      <div className={styles.subdivisionSearchBox}>
        <input
          aria-labelledby='subdivisions-and-sections--legend'
          value={sectionSearchValue}
          onChange={handleSearchChange}
          placeholder='Search'
          className={styles.subdivisionSearchInput}
        />
        <button
          onClick={(e) => toggleExpandAll(e)}
          className={styles.subdivisionSearchExpand}
        >
          {allExpanded ? 'Collapse all' : 'Expand all'}
          {!allExpanded
            ? (
              <GreenArrowDown aria-hidden />
              )
            : (
              <GreenArrowDown className={styles.inverted} aria-hidden />
              )}
        </button>
      </div>
      <div className={styles.collapsibleSectionsListContainer}>
        {(showAllSubdivisions
          ? filteredDataSubdivisions
          : filteredDataSubdivisions.slice(0, VISIBLE_LIMIT)
        ).map((subdivision) => (
          <div key={subdivision.id} className={styles.filterCheckbox}>
            <div className={styles.collapsibleSubdivision}>
              <label className={styles.expandableLabel}>
                <input
                  type='checkbox'
                  name='subdivision'
                  value={subdivision.id}
                  checked={selectedSections.get(subdivision.id)}
                  onChange={(e) =>
                    handleSubdivisionChange(e, [...subdivision.sections].length)}
                />
                {subdivision.label}
              </label>
              <button
                aria-controls={strToId(subdivision.label, 'filter-sub-accordionbody--')}
                aria-expanded={expandedSubdivisions[subdivision.id]}
                className={styles.btnExpandSubdivision}
                id={strToId(subdivision.label, 'filter-sub-toggle--')}
                onClick={() => toggleSubdivisions(subdivision.id)}
                type='button'
              >
                <ChevronDown className={`${styles.chevron}${' '}${expandedSubdivisions[subdivision.id] ? styles.chevronExpanded : ''}`} aria-hidden />
                <span className='sr-only'>{expandedSubdivisions[subdivision.id] ? 'Collapse ' : 'Expand '}{subdivision.label}</span>
              </button>
            </div>
            <div
              className={`${styles.expendedListContainer} ${!expandedSubdivisions[subdivision.id] ? styles.expendedListContainerHidden : ''}`}
              id={strToId(subdivision.label, 'filter-sub-accordionbody--')}
              aria-labelledby={strToId(subdivision.label, 'filter-sub-toggle--')}
            >
              {[...subdivision.sections].map((section) => (
                <div key={section.id} className={styles.filterCheckbox}>
                  <label className={styles.expandableLabel}>
                    <input
                      type='checkbox'
                      name='section'
                      value={section.id}
                      checked={selectedSections.get(section.id)}
                      onChange={(e) =>
                        handleSectionChange(
                          e,
                          subdivision.id,
                          [...subdivision.sections].length
                        )}
                    />
                    {section.label}
                  </label>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {filteredDataSubdivisions.length > VISIBLE_LIMIT &&
        <button
          type='button'
          onClick={(e) => {
            e.preventDefault();
            setShowAllSubdivisions((prev) => !prev);
          }}
          className={styles.showMoreButton}
        >
          {showAllSubdivisions ? 'Show less' : 'Show more'}
        </button>}
    </fieldset>
  ];

  const filterScopeOfReqsMobile = filterScopeOfReqs.toSpliced(
    1,
    0,
    <fieldset id='topic' className={styles.fieldset} key='topic-of-reqs'>
      <legend className={styles.filterSubheader}>Topic</legend>
      <div className={styles.collapsibleSectionsListContainer}>
        {topicOptions.map((topic) => {
          const containsTopic =
            topic.value === 'all' ||
            [...dataPreTopic].some((item) => item.topic === topic.value);
          const topicSelected = formData.resultsStep.topic === topic.value;
          if (containsTopic) {
            return (
              <div
                key={`${topic.id}-${formData.resultsStep.topic}`}
                className={styles.radioScopeOptions}
              >
                <label style={{ display: 'flex', lineHeight: '21.75px' }}>
                  <input
                    type='radio'
                    name='topic'
                    id={topic.id}
                    value={topic.value}
                    checked={topicSelected}
                    onChange={() => handleTopicChange(topic)}
                  />
                  {topic.label}
                </label>
              </div>
            );
          }
          return null;
        })}
      </div>
    </fieldset>
  );

  if (!shouldDisplayRequirementTypes) {
    filterScopeOfReqs.shift();
    filterScopeOfReqsMobile.shift();
  }

  const filterMobileModal = () => {
    const controllerTitles = [
      'Type of requirements',
      'Topic',
      'Subdivisions and sections'
    ];
    if (!shouldDisplayRequirementTypes) {
      controllerTitles.shift();
    }
    return (
      <div className={styles.modalBackground}>
        <div className={styles.modalContainer}>
          <div className={styles.modalTitleBar}>
            <p className={styles.modalTitle}>Filter by:</p>
            <button
              className={styles.closeModalButton}
              onClick={() => setShowFilterModal(false)}
              type='button'
            >
              <CloseIcon className={styles.modalIcon} />
            </button>
          </div>
          <div className={styles.modalContent}>
            {filterScopeOfReqsMobile.map((filter, index) =>
              filterMobileAccordion(controllerTitles[index], filter, index)
            )}
          </div>
          <div className={styles.modalButtonContainer}>
            <button
              className={styles.modalBtnClear}
              type='button'
              onClick={(e) => {
                e.preventDefault();
                setInitialRequirements();
              }}
            >
              Clear filters
            </button>
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (showExportModal === true) {
      const searchParams = new URLSearchParams(window.location.search);
      setExportURL(exportURL + searchParams);
    }
  }, [showExportModal]);

  const exportModal = () => {
    const handlePrint = async () => {
      setIsPrinting(true);

      // delay to wait for re-render
      await new Promise((resolve) => setTimeout(resolve, 100));

      window.print();

      // Put back pagination after print
      setIsPrinting(false);
    };
    // TODO fix the ui for mobile view 320px
    return (
      <div className={styles.modalBackground}>
        <div className={styles.exportModalContainer}>
          <h2 className={styles.exportModalHeading}>Export Results</h2>
          <button
            type='button'
            className={styles.exportModalCloseButton}
            onClick={() => setShowExportModal(false)}
          >
            <CloseModalIcon
              className={styles.exportModalCloseIcon}
              aria-label='close-export-modal-button'
            />
          </button>
          <p className={styles.exportModalText}>
            You can copy the link to your results or print/save them to your
            device.
          </p>
          <CopyLink displayLink={exportURL} />
          <ColorButton
            className={styles.printButton}
            label='Print / Save as PDF'
            onClick={handlePrint}
          />
          <div className={styles.exportModalButtonContainer}>
            <UnderlineButton
              label='Go back'
              onClick={() => {
                setShowExportModal(false);
              }}
            />
          </div>
        </div>
      </div>
    );
  };

  const showAllExportContent = () => {
    // TODO display all content cards expanded along with heading that describes what content is being displayed
  };

  const otherUser = formData.descriptionStep?.value;
  const isOtherUser = ['acw', 'dpo', 'rp'].includes(otherUser);

  return (
    <div className={styles.bodyContainer}>
      {showExportModal && exportModal()}
      {showExportModal && showAllExportContent()}
      {showFilterModal && filterMobileModal()}
      {showDisclaimerModal && (
        <DisclaimerModal
          setShowDisclaimerModal={setShowDisclaimerModal}
          handleGoBack={handleGoBack}
        />
      )}
      <div className={styles.headerWrapper}>
        <div className='container'>
          <div className={styles.headerContainer}>
            <h2 className='sr-only'>Results</h2>
            <ResultsSummary formData={formData} isPrinting={isPrinting} />
            <button
              type='button'
              onClick={handleEditDetails}
              className={styles.editDetailsButton}
            >
              Edit details
            </button>
          </div>
        </div>
      </div>
      <div
        id='filterAndContentContainer'
        className={styles.filterAndContentContainer}
      >
        <div id='filterContainer' className={styles.filterContainer}>
          <h3 className={styles.filterHeader}>Filter by:</h3>
          {filterScopeOfReqs.map((filter) => filter)}
          <div>
            <button
              type='button'
              onClick={setInitialRequirements}
              className={styles.resetFilterButton}
            >
              Reset all filters
            </button>
          </div>
        </div>
        <div className={styles.contentContainer}>
          <div id='results-page-topic-menu' className={styles.topicMenu}>
            {!isOtherUser && (
              <>
                <h3 className='sr-only' id='topic-menu--title'>
                  Topics
                </h3>
                <div
                  className={styles.optionsContainer}
                  role='tablist'
                  aria-labelledby='topic-menu--title'
                >
                  {topicOptions.map((topic, index) => {
                    const containsTopic =
                      topic.value === 'all' ||
                      [...dataPreTopic].some(
                        (item) => item.topic.includes(topic.value)
                      );
                    const topicSelected =
                          formData.resultsStep.topic === topic.value;
                    if (containsTopic) {
                      return (
                        <button
                          key={`${topic.label}-${index}`}
                          id={`${topic.label}-${index}`}
                          role='tab'
                          type='button'
                          aria-selected={topicSelected}
                          aria-controls='topic-content'
                          className={
                            topicSelected
                              ? styles.topicOptionSelected
                              : styles.topicOption
                          }
                          onClick={() => handleTopicChange(topic)}
                        >
                          {topic.label}
                        </button>
                      );
                    }

                    return null;
                  })}
                </div>
              </>
            )}
            <button
              id='filter-button-mobile'
              className={styles.filterButtonMobile}
              onClick={() => setShowFilterModal(!showFilterModal)}
              type='button'
            >
              <SliderIcon className={styles.filterButtonMobileIcon} />
              <p className={styles.filterButtonMobileText}>Filter</p>
            </button>
            <ExportButton
              className={styles.exportButton}
              onClick={() => {
                setShowExportModal(true);
              }}
            />
          </div>
          <p className={styles.infoText}>
            Please note: Filtering by topic may limit your results, as not all
            requirements are aligned with a specific topic. Based on this, the
            requirements are listed below.
          </p>
          <div className='results' id='topic-content' role='tabpanel'>
            {(isPrinting ? dataPostFilters : currentResults).map(
              (item, index) => (
                <ResultsCardHook
                  key={index}
                  subdivision={item.subdivision}
                  section={item.section}
                  summary={item.summary}
                  extract={item.extract}
                  citation={item.citation}
                  citationHref={item.link}
                  type={item.type}
                  requirement={item.requirement}
                  rules={item.rules}
                  item={item}
                  userIsProvider={userIsProvider}
                  pageNumber={currentPage}
                  topic={formData.resultsStep.topic }
                />
              )
            )}
          </div>

          {totalPages > 1 && !isPrinting && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      </div>
    </div>
  );
}
